import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const getHoverClasses = (variant: string) => {
    if (variant === "read" || variant === "unread") {
        return "shadow-xl bg-surface-activeFg border-card-border";
    }
    if (variant === "focusUnread") {
        return "shadow-xl bg-TypeMail-FocusDarkHover border-TypeMail-FocusDarkHoverBorder";
    }
    if (variant === "focusRead") {
        return "shadow-xl bg-TypeMail-FocusMediumHover border-TypeMail-FocusMediumHoverBorder";
    }

    return "";
};

const cardVariant = cva("border rounded-xl", {
    variants: {
        variant: {
            default: "bg-white text-text-main-title",
            dashboard:
                "border-white/30 bg-white/30 flex h-auto flex-col text-white",
            read: "border-2 bg-white/30 text-text-main-title border-white/30 hover:bg-surface-activeFg hover:border-card-border cursor-pointer shadow-sm hover:shadow-xl transition-shadow duration-300",
            unread: "border-2 bg-white text-text-main-title border-white/30 hover:bg-surface-activeFg hover:border-card-border cursor-pointer shadow-sm hover:shadow-xl transition-shadow duration-300",
            event: "bg-surface-event border-surface-event flex flex-col justify-between text-left text-black",
            focusUnread:
                "border-2 bg-TypeMail-FocusDark text-white border-TypeMail-FocusDark cursor-pointer [&_#date]:text-white [&_#icons]:text-white shadow-sm hover:shadow-xl transition-shadow duration-300 hover:border-TypeMail-FocusDarkHoverBorder hover:bg-TypeMail-FocusDarkHover",
            focusRead:
                "border-2 bg-TypeMail-FocusMedium border-TypeMail-FocusMedium cursor-pointer shadow-sm hover:shadow-xl transition-shadow duration-300 hover:border-TypeMail-FocusMediumHoverBorder hover:bg-TypeMail-FocusMediumHover",
            orange: "border-surface-brand bg-surface-brand text-white font-medium text-sm text-left",
            white: "bg-white border-white",
            license:
                "flex flex-col transition-all duration-200 cursor-pointer bg-white shadow-md border border-transparent hover:border-primary-lighter hover:shadow-lg flex-1",
        },
        size: {
            default: "p-2",
            sm: "h-full p-4",
            md: "p-8",
            event: "h-full px-3 py-2",
            "1x1": "p-4 grid-cols-1 grid-rows-1",
            "1x2": "p-4 grid-cols-1 grid-rows-2",
            "2x1": "p-4 grid-cols-2 grid-rows-1",
            "2x2": "p-4 grid-cols-2 grid-rows-2",
            "12x1": "p-2 grid-cols-12 grid-rows-1",
            "1x4": "p-3 grid-cols-1 grid-rows-4",
        },
        display: {
            default: "",
            grid: "grid",
        },
        opened: {
            true: "border-surface-calendarEventDark shadow-lg",
            false: "",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
        opened: false,
    },
});

const cardContentVariant = cva("pt-0 mt-2", {
    variants: {
        variant: {
            ghost: "p-0",
            default: "p-6",
            dashboard: "flex flex-col",
            event: "p-0 pb-2 flex flex-col text-xs gap-2 mt-1 mb-auto",
            mailInfos:
                "px-3 pb-1 pt-0 text-left font-body text-sm font-medium text-text-body",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const cardHeaderVariant = cva("flex items-center", {
    variants: {
        variant: {
            default: "",
            mailInfos:
                "gap-2 px-4 pt-2 font-body text-sm font-semibold text-text-mainTitle",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

export interface CardProps
    extends
        React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof cardVariant> {
    forceHover?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
    (
        { className, variant, size, display, opened, forceHover, ...props },
        ref,
    ) => (
        <div
            ref={ref}
            className={cn(
                cardVariant({ variant, size, display, opened, className }),
                forceHover && variant && getHoverClasses(variant),
            )}
            {...props}
        />
    ),
);
Card.displayName = "Card";

export interface CardHeaderProps
    extends
        React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof cardHeaderVariant> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
    ({ className, variant, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(cardHeaderVariant({ variant, className }))}
            {...props}
        />
    ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-base-semibold", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-sm", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> &
        VariantProps<typeof cardContentVariant>
>(({ className, variant, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(cardContentVariant({ variant, className }))}
        {...props}
    />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("", className)} {...props} />
));
CardFooter.displayName = "CardFooter";

export {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardDescription,
    CardContent,
};
